import { CreditCard, Check, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useSubscription';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { useMyClinicRole } from '@/hooks/useMyClinicRole';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';
import { toast } from '@/hooks/use-toast';

export default function SettingsBilling() {
  const { role, isLoading: roleLoading } = useMyClinicRole();
  const { data: subscription, isLoading } = useSubscription();
  const { data: statusData } = useSubscriptionStatus();
  const [portalLoading, setPortalLoading] = useState(false);

  const status = statusData?.status;
  const badge =
    status === 'active'
      ? { label: 'Active', className: 'bg-green-100 text-green-800' }
      : status === 'trialing'
      ? { label: 'Trialing', className: 'bg-green-100 text-green-800' }
      : status === 'past_due'
      ? { label: 'Past Due', className: 'bg-yellow-100 text-yellow-800' }
      : status === 'soft_closed' || status === 'unpaid'
      ? { label: 'Cancelled', className: 'bg-gray-200 text-gray-700' }
      : { label: 'Incomplete', className: 'bg-gray-200 text-gray-700' };

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (role !== 'owner') {
    return <Navigate to="/settings/my-profile" replace />;
  }

  const displayAmount = subscription?.amount ?? 249;
  const displayInterval = subscription?.interval ?? 'month';

  async function handleManageBilling() {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'create-billing-portal-session',
        { method: 'POST' }
      );
      if (error || !data?.url) {
        throw new Error(error?.message ?? 'Failed to open billing portal');
      }
      window.location.href = data.url;
    } catch (err) {
      console.error('Billing portal error:', err);
      toast({
        title: 'Could not open billing portal',
        description: 'Please try again or contact support@veracord.co.',
        variant: 'destructive',
      });
      setPortalLoading(false);
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Billing</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Subscription and payment management
        </p>
      </div>

      <div className="space-y-6">
        <div className="rounded-lg border border-gray-200 dark:border-border bg-white dark:bg-background p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Current Plan
              </p>
              <p className="text-xl font-bold text-foreground mt-1">Veracord</p>
              {isLoading ? (
                <div className="flex items-center gap-2 mt-1">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading plan details…</span>
                </div>
              ) : (
                <p className="text-2xl font-extrabold text-foreground">
                  ${displayAmount}{' '}
                  <span className="text-sm font-normal text-muted-foreground">
                    / {displayInterval}
                  </span>
                </p>
              )}
            </div>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </div>

          <div className="border-t pt-4">
            <p className="text-sm font-medium text-foreground mb-2">Includes:</p>
            <ul className="space-y-1.5">
              {[
                'Unlimited policies',
                'Unlimited team members',
                'Unlimited audit exports',
                'Policy Builder',
                'Email notifications',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Button
          variant="outline"
          className="w-full"
          onClick={handleManageBilling}
          disabled={portalLoading}
        >
          {portalLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <CreditCard className="mr-2 h-4 w-4" />
          )}
          {portalLoading ? 'Opening billing portal…' : 'Manage Subscription'}
          {!portalLoading && <ExternalLink className="ml-2 h-3 w-3 text-muted-foreground" />}
        </Button>
      </div>
    </div>
  );
}
